import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ReportingEntity } from '../_models/reporting-entity.model';

export interface GlobalFilter {
  reportingEntityId: number;
  reportingEntityName: string;
  divisionIds: number[];
}

@Injectable({
  providedIn: 'root'
})
export class GlobalFilterService {
  private readonly STORAGE_KEY = 'globalReportingEntityFilter';

  private globalFilterSubject = new BehaviorSubject<GlobalFilter | null>(this.loadFromStorage());

  public globalFilter$ = this.globalFilterSubject.asObservable();
  public isActive$: Observable<boolean> = this.globalFilter$.pipe(map(f => !!f));

  constructor(private authService: AuthService) {}

  /**
   * Returns true if the current user is an internal user who can use the global filter.
   * Internal users are those who do NOT require division filtering.
   */
  canUseGlobalFilter(): boolean {
    return !this.authService.requiresDivisionFiltering();
  }

  getGlobalFilter(): GlobalFilter | null {
    return this.globalFilterSubject.value;
  }

  setGlobalFilter(re: ReportingEntity): void {
    const filter: GlobalFilter = {
      reportingEntityId: re.id!,
      reportingEntityName: re.name,
      divisionIds: re.divisions ? re.divisions.map(d => d.id) : []
    };
    this.saveToStorage(filter);
    this.globalFilterSubject.next(filter);
  }

  /**
   * Returns the reporting entities completely encompassed by the given global
   * filter — i.e. every one of the entity's divisions is within the filter's
   * divisionIds. The globally-selected entity is itself included.
   */
  getEncompassedReportingEntities(filter: GlobalFilter, entities: ReportingEntity[]): ReportingEntity[] {
    const filterDivisionIds = new Set(filter.divisionIds);
    return entities.filter(re =>
      !!re.divisions && re.divisions.length > 0 &&
      re.divisions.every(d => filterDivisionIds.has(d.id))
    );
  }

  clearGlobalFilter(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.globalFilterSubject.next(null);
  }

  private loadFromStorage(): GlobalFilter | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      localStorage.removeItem(this.STORAGE_KEY);
    }
    return null;
  }

  private saveToStorage(filter: GlobalFilter): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filter));
    } catch (error) {
      console.error('Error saving global filter to localStorage:', error);
    }
  }
}
